<script setup lang="ts">
import type { AutoCompleteCompleteEvent } from "primevue/autocomplete";
import AutoComplete from "primevue/autocomplete";
import { computed, nextTick, ref } from "vue";
import type { TimezoneOption } from "@/util/timezones";
import { searchTimezones } from "@/util/timezones";
import HighlightedText from "./HighlightedText.vue";
import { autoCompletePt } from "./styles";
import type { TimezoneSelectProps } from "./types";
import { centerSelectedRow } from "./utils";

const props = withDefaults(defineProps<TimezoneSelectProps>(), {
  modelValue: undefined,
  invalid: false,
  placeholder: "Search a city, country, or time zone",
});

const emit = defineEmits<(e: "update:modelValue", value: TimezoneOption | undefined) => void>();

const query = ref<string>("");
const suggestions = ref<TimezoneOption[]>([]);

const model = computed<TimezoneOption | undefined>({
  get: () => props.modelValue,
  // forceSelection reports a cleared value as null
  set: (value) => emit("update:modelValue", value ?? undefined),
});

const search = (event: AutoCompleteCompleteEvent): void => {
  // On focus the input still holds the committed label; treat that as an
  // empty query so the full list opens instead of the single selected row.
  const raw = event.query ?? "";
  query.value = raw === props.modelValue?.formatted ? "" : raw;
  suggestions.value = searchTimezones(query.value);
};

// AutoComplete's first-open path sets the focused option but never scrolls the
// panel to it. Hook before-show (the show event waits on the enter transition
// and can miss entirely) and find the row by data attribute — the panel's core
// class doesn't render because the app mounts PrimeVue unstyled.
const scrollToSelected = (): void => {
  void nextTick(() => {
    const row = document.querySelector<HTMLElement>('li[data-p-highlight="true"]');
    if (row) centerSelectedRow(row);
  });
};
</script>

<template>
  <AutoComplete
    v-model="model"
    :suggestions="suggestions"
    option-label="formatted"
    data-key="timeZone"
    auto-option-focus
    :placeholder="props.placeholder"
    :class="[
      'w-full',
      props.invalid
        ? 'border-red-700 focus-within:ring-red-700/20'
        : 'border-gray-300 focus-within:border-teal-900 focus-within:ring-teal-900/20',
    ]"
    dropdown
    dropdown-mode="blank"
    force-selection
    complete-on-focus
    empty-search-message="No matching time zone"
    :pt="autoCompletePt"
    @complete="search"
    @before-show="scrollToSelected"
  >
    <template #option="{ option }">
      <div class="flex w-full items-baseline gap-2.5 text-sm">
        <span class="min-w-0 flex-1 truncate">
          <HighlightedText :text="option.city" :query="query" />
          <span v-if="option.country" class="ml-1.5 text-xs text-gray-500">
            <HighlightedText :text="option.country" :query="query" />
          </span>
        </span>
        <span class="shrink-0 text-xs text-gray-500">
          <HighlightedText :text="option.zoneName" :query="query" />
        </span>
        <span class="w-24 shrink-0 text-right text-xs font-semibold tabular-nums text-teal-900">
          <HighlightedText :text="option.offsetLabel" :query="query" />
        </span>
      </div>
    </template>
  </AutoComplete>
</template>
